import type { SceneV2 } from "@bezpart-board/shared";
import { bumpSpatialSceneStamp } from "../spatialIndex.js";
import type { FlatBoardOp } from "./opsTypes.js";

export type UndoEntry = {
  forward: FlatBoardOp[];
  inverse: FlatBoardOp[];
};

const MAX_UNDO = 100;

export class UndoStack {
  private undo: UndoEntry[] = [];
  private redo: UndoEntry[] = [];

  push(entry: UndoEntry): void {
    this.undo.push(entry);
    if (this.undo.length > MAX_UNDO) {
      this.undo.shift();
    }
    this.redo = [];
  }

  canUndo(): boolean {
    return this.undo.length > 0;
  }

  canRedo(): boolean {
    return this.redo.length > 0;
  }

  popUndo(): UndoEntry | undefined {
    const entry = this.undo.pop();
    if (entry) this.redo.push(entry);
    return entry;
  }

  popRedo(): UndoEntry | undefined {
    const entry = this.redo.pop();
    if (entry) this.undo.push(entry);
    return entry;
  }

  clear(): void {
    this.undo = [];
    this.redo = [];
  }
}

export type SceneStoreSnapshot = {
  scene: SceneV2;
  revision: number;
  selectedIds: string[];
};

export type SceneStoreListener = () => void;

export type SceneStoreCommitOptions = {
  /** Не записывать в undo (remote ops). */
  skipUndo?: boolean;
  undoEntry?: UndoEntry;
};

/**
 * In-memory scene + revision. Optimistic apply; server confirm via setRemoteSnapshot.
 */
export class SceneStore {
  private scene: SceneV2;
  private revision: number;
  private selectedIds: string[] = [];
  private listeners = new Set<SceneStoreListener>();
  readonly undoStack = new UndoStack();

  constructor(initial: { scene: SceneV2; revision: number; selectedIds?: string[] }) {
    this.scene = {
      version: initial.scene?.version ?? 2,
      objects: Array.isArray(initial.scene?.objects) ? initial.scene.objects : [],
    };
    this.revision = initial.revision;
    this.selectedIds = initial.selectedIds?.slice() ?? [];
  }

  subscribe(listener: SceneStoreListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  getSnapshot(): SceneStoreSnapshot {
    return {
      scene: this.scene,
      revision: this.revision,
      selectedIds: this.selectedIds.slice(),
    };
  }

  getScene(): SceneV2 {
    return this.scene;
  }

  getRevision(): number {
    return this.revision;
  }

  getSelection(): string[] {
    return this.selectedIds.slice();
  }

  setSelection(ids: string[]): void {
    this.selectedIds = ids.slice();
    this.emit();
  }

  /** Локальное применение ops (optimistic). */
  applyLocalScene(nextScene: SceneV2, opts?: SceneStoreCommitOptions): void {
    this.scene = nextScene;
    if (opts?.undoEntry && !opts.skipUndo) {
      this.undoStack.push(opts.undoEntry);
    }
    bumpSpatialSceneStamp();
    this.emit();
  }

  setRemoteSnapshot(revision: number, scene: SceneV2, opts?: { clearUndo?: boolean }): void {
    this.revision = revision;
    this.scene = {
      version: scene?.version ?? 2,
      objects: Array.isArray(scene?.objects) ? scene.objects : [],
    };
    this.selectedIds = this.selectedIds.filter((id) =>
      this.scene.objects.some((o) => o.id === id)
    );
    if (opts?.clearUndo) {
      this.undoStack.clear();
    }
    bumpSpatialSceneStamp();
    this.emit();
  }

  /** Подтверждение revision с сервера без замены scene (optimistic уже применён). */
  confirmRevision(revision: number): void {
    if (!Number.isFinite(revision) || revision <= this.revision) return;
    this.revision = revision;
  }

  rollback(scene: SceneV2, revision: number): void {
    this.scene = scene;
    this.revision = revision;
    bumpSpatialSceneStamp();
    this.emit();
  }
}
